import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { IntlWrapper } from "./IntlUtils.js";
import RequestInvoiceBookingInput from "./RequestInvoiceBookingInput";
import RequestInvoiceTypeSelect from "./RequestInvoiceTypeSelect";
import RequestInvoice from "./RequestInvoice";
import Dashboard from "./Dashboard";
import Login from "./Login";
import ResetPassword from "./ResetPassword";
import { ConfigProvider, App as AntdApp } from "antd";
import { COLORS } from "./constants";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

// Wrap Routes with Sentry for route change tracking
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

dayjs().format();

const { REACT_APP_BRAND_NAME } = process.env;

// Error boundary fallback component with intl support
function ErrorFallback({ error, resetError }) {
  const intl = useIntl();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#f5f5f5",
        padding: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "white",
          padding: "40px",
          borderRadius: "10px",
          border: "1px solid #eeeeee",
          maxWidth: "500px",
          textAlign: "center",
        }}
      >
        <h1 style={{ margin: "0 0 10px 0", fontSize: "24px" }}>
          {intl.formatMessage({ id: "error-boundary-title" })}
        </h1>
        <p style={{ color: "#666", marginBottom: "30px", lineHeight: 1.5 }}>
          {intl.formatMessage({ id: "generic-error" })}
        </p>
        <button
          onClick={() => {
            resetError();
            window.location.href = "/";
          }}
          style={{
            padding: "10px 30px",
            backgroundColor: COLORS[REACT_APP_BRAND_NAME] || "#1890ff",
            color: "white",
            border: "none",
            borderRadius: "10px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          {intl.formatMessage({ id: "close" })}
        </button>
      </div>
    </div>
  );
}

function App() {
  const location = useLocation();

  // Track route changes and set context for all logs
  useEffect(() => {
    // Set current path as context for all future logs
    Sentry.setContext("navigation", {
      path: location.pathname,
      search: location.search,
    });
    Sentry.logger.debug("Route changed", { path: location.pathname });
  }, [location]);

  function checkAuth() {
    const hasToken = localStorage.getItem("token");
    const hasExpiry = localStorage.getItem("expiry");

    if (hasToken && hasExpiry) {
      const today = dayjs();
      const expiry = localStorage.getItem("expiry");
      const isValid = today.isBefore(expiry);

      if (!isValid) {
        Sentry.logger.info("Auth token expired", {
          expiry,
          currentTime: today.format(),
        });
      }

      return isValid;
    }

    Sentry.logger.debug("No auth token found");
    return false;
  }

  const dashboardElement = !checkAuth() ? (
    <Navigate to="/login" state={{ from: location }} replace />
  ) : (
    <Dashboard />
  );

  return (
    <IntlWrapper>
      <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: COLORS[REACT_APP_BRAND_NAME] || "#1890ff",
              colorInfo: COLORS[REACT_APP_BRAND_NAME] || "#1890ff",
              borderRadius: 2,
            },
          }}
        >
          <AntdApp>
            <SentryRoutes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/reset/:resetUUID" element={<ResetPassword />} />
              <Route
                path="/dashboard/:rentalId/:landlordId/:yearFilter/:monthFilter/:tab"
                element={dashboardElement}
              />
              {/* Legacy tab-less URLs - Dashboard redirects them to the default tab */}
              <Route
                path="/dashboard/:rentalId/:landlordId/:yearFilter/:monthFilter"
                element={dashboardElement}
              />
              <Route path="/dashboard" element={dashboardElement} />
              <Route
                path="/request-invoice"
                element={<RequestInvoiceBookingInput />}
              />
              <Route
                path="/request-invoice/:bookingsync_booking_id/"
                element={<RequestInvoiceTypeSelect />}
              />
              <Route
                path="/request-invoice/:bookingsync_booking_id/:type"
                element={<RequestInvoice />}
              />
              <Route
                path="*"
                element={
                  <Navigate to="/login" state={{ from: location }} replace />
                }
              />
            </SentryRoutes>
          </AntdApp>
        </ConfigProvider>
      </Sentry.ErrorBoundary>
    </IntlWrapper>
  );
}

export default App;
